import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import PageHeader from './_components/PageHeader';
/* ---------- Icons ---------- */

const I = ({ d, className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CardIcon     = (p) => <I {...p} d="M3 6h18v12H3zM3 10h18M7 15h4" />;
const BellIcon     = (p) => <I {...p} d="M12 3a5 5 0 00-5 5v3.5L5.5 15h13L17 11.5V8a5 5 0 00-5-5zM10 18a2 2 0 004 0" />;
const SearchIcon   = (p) => <I {...p} d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-3.5-3.5" />;
const ChatIcon     = (p) => <I {...p} d="M4 5h16v11H8l-4 4V5z" />;
const CheckIcon    = (p) => <I {...p} d="M5 12l4 4 10-10" />;

/* ---------- Helpers ---------- */

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDateLong = (d = new Date()) =>
  d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const last4 = (masked) => {
  if (!masked) return '----';
  const digits = String(masked).replace(/\D/g, '');
  return digits.slice(-4) || '----';
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

/* ---------- Shared header block (greeting → search) ---------- */

/* ---------- Account card ---------- */

const AccountCard = ({ account }) => {
  const type = (account.accountType || 'Checking').toUpperCase();
  const status = (account.status || 'active').toUpperCase();
  const number = last4(account.accountNumberMasked);
  const balance = account.balance ?? 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-start gap-4">
        {/* Icon tile */}
        <div className="w-14 h-14 rounded-xl bg-[#0b2740] text-white flex items-center justify-center shrink-0">
          <CardIcon className="w-6 h-6" />
        </div>

        {/* Text block */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.18em] uppercase text-gray-500">
            {type}
          </div>
          <div className="text-[22px] font-bold text-[#1a2b3c] leading-tight mt-1 truncate">
            {fmtMoney(balance)}
          </div>
          <div className="text-[12px] text-gray-500 mt-1">
            Account number ---- {number}
          </div>
        </div>
      </div>

      {/* Status pill */}
      <div className="mt-4">
        <span className="inline-block bg-[#e6e8f4] text-[#0b2740] text-[10px] font-semibold tracking-[0.14em] uppercase px-3 py-1.5 rounded-full">
          {status}
        </span>
      </div>
    </div>
  );
};

/* ---------- Loading skeleton ---------- */

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 animate-pulse">
    <div className="flex items-start gap-4">
      <div className="w-14 h-14 rounded-xl bg-gray-200 shrink-0" />
      <div className="flex-1">
        <div className="h-3 w-16 bg-gray-200 rounded" />
        <div className="h-5 w-28 bg-gray-200 rounded mt-2" />
        <div className="h-3 w-40 bg-gray-200 rounded mt-2" />
      </div>
    </div>
    <div className="mt-4 h-6 w-20 bg-gray-200 rounded-full" />
  </div>
);

/* ---------- Page ---------- */

const FintechAccounts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/fintech/dashboard');
        if (alive) setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const holderName = data?.accountHolderName || '';
  const accounts = data?.linkedAccounts || [];

  return (
    <div className="px-4 pt-5 pb-8 lg:px-8 lg:pt-8 max-w-3xl mx-auto">
      {/* Header block (greeting, date, secure session, search) */}
      <PageHeader holderName={holderName} />

      {/* Title section */}
      <div className="mb-5">
        <h2 className="font-serif text-[24px] leading-tight text-[#1a2b3c]">
          Your Accounts
        </h2>
        <p className="text-[13px] text-gray-500 mt-1">
          Balances and account details for your FDIC-linked accounts.
        </p>
      </div>

      {/* Loading */}
      {loading && <SkeletonCard />}

      {/* Empty */}
      {!loading && accounts.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-xl bg-[#0b2740]/5 text-[#0b2740] flex items-center justify-center mx-auto mb-3">
            <CardIcon className="w-6 h-6" />
          </div>
          <div className="text-[14px] font-semibold text-[#1a2b3c]">
            No linked accounts yet
          </div>
          <p className="text-[12px] text-gray-500 mt-1">
            Once an account is linked to your case, it will appear here.
          </p>
        </div>
      )}

      {/* List */}
      {!loading && accounts.length > 0 && (
        <div className="space-y-4">
          {accounts.map((a, idx) => (
            <AccountCard key={a.id || idx} account={a} />
          ))}
        </div>
      )}
    </div>
  );
};

export default FintechAccounts;